// Holds one client file's Solid template data on window.silverfinSolidData as
// plain JSON. Solid templates are compiled outside the monolith's JavaScript,
// so they read the data from the window instead of importing this module.
//
// The loader fetches when it holds nothing for the client file or the page's
// period, and refreshes when a Solid template is shown. A refresh sends the
// versions the loader holds, and the server ships only the resources that
// changed, so held data stays readable meanwhile and listeners hear only
// about real changes.
import type { ResponseError } from '@app/utils/types'

import SFetch from '@app/utils/sfetch'

export const SILVERFIN_SOLID_DATA_EVENT = 'silverfin:solid-data-loaded'

// Mirrors the endpoint's resource_versions parameter and its versions.
interface ResourceVersions {
  company: Record<string, string>
  periods: Record<string, Record<string, string>>
}

// The solid_data endpoint's response: the data of the resources that changed,
// company-wide ones under company and the requested period's under periods,
// plus the current version of every resource.
interface SolidDataResponse {
  data: {
    company: Record<string, unknown>
    periods: Record<string, Record<string, unknown>>
  }
  versions: ResourceVersions
}

// The held data: every resource's data merged out of the responses, with
// the client file's ids stamped on.
interface ClientFileData {
  firmId: string | number
  companyId: string | number
  company: Record<string, unknown>
  periods: Record<string, Record<string, unknown>>
}

export interface SolidDataState {
  status: 'pending' | 'error' | 'success'
  isFetching: boolean
  data?: ClientFileData
  error?: Error | ResponseError
}

export interface SolidDataLoader {
  loadData: (
    firmId: string | number,
    companyId: string | number,
    ledgerId?: string | number,
    reconciliationTextId?: string | number
  ) => Promise<void>
  get: (firmId: string | number, companyId: string | number) => SolidDataState
}

export default function createSolidDataLoader(): SolidDataLoader {
  let cachedCompanyKey: string | null = null
  let latestLoad = 0
  let inflight: Promise<void> | null = null
  let state: SolidDataState = { status: 'pending', isFetching: false }
  let resourceVersions: ResourceVersions = { company: {}, periods: {} }
  let inflightLedgerId: string | number | null | undefined = null

  const loadData: SolidDataLoader['loadData'] = async (
    firmId,
    companyId,
    ledgerId,
    reconciliationTextId
  ) => {
    const requestedCompanyKey = `${firmId}/${companyId}`

    if (cachedCompanyKey === requestedCompanyKey) {
      if (joinsInflight(ledgerId)) return inflight ?? undefined
      await inflight

      if (cachedCompanyKey !== requestedCompanyKey) return
      // Another call for this period got in while this one waited.
      if (joinsInflight(ledgerId)) return inflight ?? undefined
      if (!shouldFetch(ledgerId, reconciliationTextId)) return

      state = { ...state, isFetching: true }
    } else {
      cachedCompanyKey = requestedCompanyKey

      resourceVersions = { company: {}, periods: {} }
      state = { status: 'pending', isFetching: true }
    }

    const loadId = ++latestLoad
    inflightLedgerId = ledgerId

    const request = (async (): Promise<void> => {
      try {
        const url = window.Silverfin.Routes.solid_data_path(firmId, companyId, {
          ...(ledgerId !== undefined ? { ledger_id: ledgerId } : {}),
          // Only the requested period's versions travel: the server compares
          // nothing else, and the query string must stay small.
          resource_versions: JSON.stringify({
            company: resourceVersions.company,
            periods:
              ledgerId !== undefined &&
              resourceVersions.periods[String(ledgerId)]
                ? {
                    [String(ledgerId)]:
                      resourceVersions.periods[String(ledgerId)],
                  }
                : {},
          }),
        })

        const body = await SFetch.json<SolidDataResponse>(
          url,
          {},
          { camelizeJSON: false }
        )
        if (!body) throw new Error(`Preload fetch returned no body for ${url}`)
        if (loadId !== latestLoad) return

        inflightLedgerId = null
        if (merge(body, firmId, companyId)) {
          document.dispatchEvent(
            new CustomEvent(SILVERFIN_SOLID_DATA_EVENT, {
              detail: { firmId, companyId },
            })
          )
        }
      } catch (error) {
        if (loadId !== latestLoad) return

        inflightLedgerId = null
        state = {
          status: 'error',
          isFetching: false,
          data: state.data,
          error: error as Error | ResponseError,
        }
      }
    })()

    inflight = request
    return request
  }

  const get: SolidDataLoader['get'] = (firmId, companyId) => {
    if (cachedCompanyKey !== `${firmId}/${companyId}`)
      return { status: 'pending', isFetching: false }

    return { ...state }
  }

  const shouldFetch = (
    ledgerId?: string | number,
    reconciliationTextId?: string | number
  ): boolean => {
    if (state.status !== 'success') return true
    if (reconciliationTextId !== undefined) return true
    return (
      ledgerId !== undefined &&
      !(String(ledgerId) in (state.data?.periods ?? {}))
    )
  }

  // True when a request for this period is still in flight. The page load and
  // the template mount both ask for the same period, so the second call joins
  // the first.
  const joinsInflight = (ledgerId?: string | number): boolean =>
    inflightLedgerId !== null && String(inflightLedgerId) === String(ledgerId)

  // Applies the changed resources over the held data and takes the versions
  // as the server states them. Returns whether any resource changed.
  const merge = (
    body: SolidDataResponse,
    firmId: string | number,
    companyId: string | number
  ): boolean => {
    const periods = { ...state.data?.periods }
    for (const [ledgerId, resources] of Object.entries(body.data.periods)) {
      periods[ledgerId] = { ...periods[ledgerId], ...resources }
    }
    resourceVersions = {
      company: body.versions.company,
      periods: { ...resourceVersions.periods, ...body.versions.periods },
    }
    state = {
      status: 'success',
      isFetching: false,
      data: {
        firmId,
        companyId,
        company: { ...state.data?.company, ...body.data.company },
        periods,
      },
    }

    return (
      Object.keys(body.data.company).length > 0 ||
      Object.values(body.data.periods).some(
        (resources) => Object.keys(resources).length > 0
      )
    )
  }

  return { loadData, get }
}
